import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Button, Icon, IconButton, IconName, styles } from "stories/Admin";

interface ContentModalProps {
  open: boolean;
  title: React.ReactNode;
  icon?: IconName;
  width?: number;
  saving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  onSave: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Page-local edit/create dialog built with the library's modal classes
 * (overlay/modal/mh/mb/mf). Used by the superadmin content pages.
 */
const ContentModal: React.FC<ContentModalProps> = ({
  open,
  title,
  icon = "edit",
  width = 680,
  saving,
  saveLabel = "Lagre endringer",
  cancelLabel = "Avbryt",
  onSave,
  onClose,
  children,
}) => {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>("input, textarea, select")?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      style={{ paddingTop: 70 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div
        ref={ref}
        className={styles.modal}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !saving) {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 className={styles.mh} id={titleId}>
          <span className={styles.mhIcon} aria-hidden="true">
            <Icon name={icon} size={18} strokeWidth={2} />
          </span>
          {title}
          <IconButton
            label="Lukk"
            icon={<Icon name="x" size={18} strokeWidth={2} />}
            onClick={onClose}
            disabled={saving}
            style={{ marginLeft: "auto" }}
          />
        </h2>
        <div className={styles.mb}>{children}</div>
        <div className={styles.mf}>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {cancelLabel}
          </Button>
          <Button onClick={onSave} loading={saving}>
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ContentModal;
